import Icon, { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Popover, Typography } from "antd";
import { ReactNode } from "react";
import styled, { css } from "styled-components";
import { colors } from "styles/colors";
import { stopPropagation } from "utils/javascript";

const PopoverLinkStyles = css`
  color: #1890ff;
  cursor: pointer;
`;

export const StyledRoutingRuleNamePreview = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  column-gap: 8px;
  row-gap: 4px;
  font-weight: 600;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const StyledRoutingRulePreview = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: min-content 1fr;
  column-gap: 8px;
  row-gap: 4px;

  &.link-styled {
    grid-template-columns: min-content 1fr min-content;

    .info {
      ${PopoverLinkStyles}
    }
  }

  .icon-container {
    grid-column: 1;
    grid-row: 1;
    height: 22px;
    width: 20px;
  }

  .label {
    grid-column: 2;
    grid-row: 1;
    margin-bottom: 0;

    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .filters {
    grid-column: 2;
    grid-row: 2;
    margin-bottom: 0;
  }

  img,
  .anticon {
    grid-column: 1;
    grid-row: 1;
    font-size: 16px;
    width: 20px;
  }
`;

export const StyledRoutingRulePreviewComponent: React.FC<{
  icon: typeof Icon;
  label: string;
  content?: ReactNode;
}> = ({ icon: Icon, label, content }) => {
  return (
    <Flex vertical align="flex-start">
      <Flex align="center" justify="center" gap="8px">
        <Icon className="w-5" />
        <Typography.Text>{label}</Typography.Text>
      </Flex>
      {content && <div style={{ marginTop: 20, width: "100%" }}>{content}</div>}
    </Flex>
  );
};

export const RoutingRulePopover: React.FC<{
  icon: ReactNode;
  text: string;
  popoverContent?: ReactNode;
}> = ({ icon, text, popoverContent }) => (
  <StyledRoutingRulePreview>
    <div className="icon-container">{icon}</div>
    <Flex gap={8}>
      <span>{text}</span>
      {popoverContent && (
        <Popover
          content={
            <div style={{ maxWidth: 350 }} onClick={stopPropagation}>
              {popoverContent}
            </div>
          }
          trigger="hover"
        >
          <InfoCircleOutlined
            style={{ color: colors.status.info.light, cursor: "pointer" }}
            onClick={stopPropagation}
          />
        </Popover>
      )}
    </Flex>
  </StyledRoutingRulePreview>
);

/**
 * Styles for Rule Editor
 */

export const StyledRuleMetadataEditor = styled.div`
  display: grid;
  align-items: center;
  text-align: right;
  column-gap: 8px;
  row-gap: 8px;
  grid-template-columns: max-content 1fr;
`;

export const StyledResourceFilterCount = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 8px;

  .text {
    grid-column: 1;
    grid-row: 1;
    margin-bottom: 0;
  }

  .popover {
    color: rgba(
      0,
      0,
      0,
      0.45
    ); // Matches the color used for "secondary" text in Ant Design
    grid-column: 2;
    grid-row: 1;
  }
`;

export const StyledResourceFilterEdit = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: 150px 1fr min-content;
  column-gap: 8px;
  row-gap: 8px;
  margin-bottom: 12px;
  max-width: 800px;

  .filter-key-and-pattern {
    grid-column: 2;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .filter-cancel {
    grid-column: 3;
    grid-row: 1;
    justify-self: end;
  }

  .filter-part {
    display: grid;
    align-items: center;
    grid-template-columns: 150px 1fr;
    column-gap: 8px;
  }
`;
